import apiClient from './api';

/**
 * Holiday Management API Service
 */
export const holidayAPI = {
    /**
     * Get all holidays with optional filters
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getHolidays: (params = {}) => {
        return apiClient.get('holidays', { params });
    },

    /**
     * Get weekly holidays
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getWeeklyHolidays: (params = {}) => {
        return apiClient.get('holidays/weekly', { params });
    },

    /**
     * Create a new holiday
     * @param {Object} data - Holiday data
     * @returns {Promise} API response
     */
    createHoliday: (data) => {
        return apiClient.post('holidays', data);
    },

    /**
     * Update an existing holiday
     * @param {number} id - Holiday ID
     * @param {Object} data - Updated holiday data
     * @returns {Promise} API response
     */
    updateHoliday: (id, data) => {
        return apiClient.put(`holidays/${id}`, data);
    },

    /**
     * Delete a holiday
     * @param {number} id - Holiday ID
     * @returns {Promise} API response
     */
    deleteHoliday: (id) => {
        return apiClient.delete(`holidays/${id}`);
    },

    /**
     * Create weekly holidays
     * @param {Object} data - Weekly holiday data
     * @returns {Promise} API response
     */
    createWeeklyHolidays: (data) => {
        return apiClient.post('holidays/weekly', data);
    },

    /**
     * Delete a weekly holiday
     * @param {number} id - Weekly holiday ID
     * @returns {Promise} API response
     */
    deleteWeeklyHoliday: (id) => {
        return apiClient.delete(`holidays/weekly/${id}`);
    },

    /**
     * Get classes for dropdown
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getClasses: (params = {}) => {
        return apiClient.get('holidays/classes', { params });
    },

    /**
     * Check if a date is a holiday
     * @param {Object} data - Date and class information
     * @returns {Promise} API response
     */
    checkHoliday: (data) => {
        return apiClient.post('holidays/check', data);
    },

    /**
     * Get holidays for a specific month
     * @param {Object} params - Month and other filter parameters
     * @returns {Promise} API response
     */
    getHolidaysForMonth: (params = {}) => {
        return apiClient.get('holidays', { params });
    },

    /**
     * Get holidays for a date range
     * @param {Object} params - Date range and other filter parameters
     * @returns {Promise} API response
     */
    getHolidaysForDateRange: (params = {}) => {
        return apiClient.get('holidays', { params });
    }
};

/**
 * Holiday helper functions
 */
export const holidayHelpers = {
    /**
     * Format holiday data for display
     * @param {Object} holiday - Holiday object
     * @returns {Object} Formatted holiday data
     */
    formatHolidayForDisplay: (holiday) => {
        return {
            ...holiday,
            formattedDate: new Date(holiday.holiday_date).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            displayType: holiday.holiday_type.charAt(0).toUpperCase() + holiday.holiday_type.slice(1),
            scopeText: holiday.holiday_type === 'institution' ? 'Institution-wide' : 
                      holiday.class ? `Class: ${holiday.class.class_name}` : 'All Classes'
        };
    },

    /**
     * Validate holiday data
     * @param {Object} data - Holiday data to validate
     * @returns {Array} Array of validation errors
     */
    validateHolidayData: (data) => {
        const errors = [];

        if (!data.holiday_name || data.holiday_name.trim() === '') {
            errors.push('Holiday name is required');
        }

        if (!data.holiday_date) {
            errors.push('Holiday date is required');
        }

        if (!data.holiday_type) {
            errors.push('Holiday type is required');
        }

        if (data.holiday_type === 'class' && !data.class_id) {
            errors.push('Class is required for class-wise holidays');
        }

        return errors;
    },

    /**
     * Get holiday type badge class
     * @param {string} type - Holiday type
     * @returns {string} Badge class
     */
    getHolidayTypeBadge: (type) => {
        const badgeMap = {
            'institution': 'badge bg-primary',
            'class': 'badge bg-success',
            'weekly': 'badge bg-secondary'
        };
        return badgeMap[type] || 'badge bg-secondary';
    },

    /**
     * Check if holiday is editable
     * @param {Object} holiday - Holiday object
     * @returns {boolean} Whether holiday can be edited
     */
    isHolidayEditable: (holiday) => {
        // All holidays (past, present, future) are editable
        return true;
    },

    /**
     * Get days of week for weekly holidays
     * @returns {Array} Array of day objects
     */
    getDaysOfWeek: () => {
        return [
            { value: 'sunday', label: 'Sunday', shortLabel: 'Sun' },
            { value: 'monday', label: 'Monday', shortLabel: 'Mon' },
            { value: 'tuesday', label: 'Tuesday', shortLabel: 'Tue' },
            { value: 'wednesday', label: 'Wednesday', shortLabel: 'Wed' },
            { value: 'thursday', label: 'Thursday', shortLabel: 'Thu' },
            { value: 'friday', label: 'Friday', shortLabel: 'Fri' },
            { value: 'saturday', label: 'Saturday', shortLabel: 'Sat' }
        ];
    },

    /**
     * Format weekly holiday for display
     * @param {Object} weeklyHoliday - Weekly holiday object
     * @returns {Object} Formatted weekly holiday data
     */
    formatWeeklyHolidayForDisplay: (weeklyHoliday) => {
        return {
            ...weeklyHoliday,
            displayDay: weeklyHoliday.day_of_week.charAt(0).toUpperCase() + weeklyHoliday.day_of_week.slice(1),
            scopeText: weeklyHoliday.class ? `Class: ${weeklyHoliday.class.class_name}` : 'Institution-wide'
        };
    },

    /**
     * Group holidays by month
     * @param {Array} holidays - Array of holidays
     * @returns {Object} Holidays grouped by month
     */
    groupHolidaysByMonth: (holidays) => {
        const grouped = {};
        holidays.forEach(holiday => {
            const date = new Date(holiday.holiday_date);
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!grouped[monthYear]) {
                grouped[monthYear] = [];
            }
            grouped[monthYear].push(holiday);
        });
        return grouped;
    }
};

export default holidayAPI;
